import { useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import "./Shell.css";
import CompPage from "./App"; // App.js IS the comp page (unchanged) - imported here as CompPage.
import JDSkillsPage from "./JDSkillsPage";
import AgentPage from "./AgentPage";
import TrendsPage from "./TrendsPage";
import ComparePage from "./ComparePage";
import GatewayPage from "./GatewayPage";
import LoginPage from "./LoginPage";
import { setNavFrom } from "./navSource";

/**
 * Shell
 *
 * App-wide chrome: a left icon rail + a topbar, with one page shown in the
 * content area at a time. Ported from the prototype's `<nav class="rail">` +
 * `.main`/`.topbar` structure (Minerva_intelligence_UI_prototype.html).
 *
 * Only the pages we've actually built get a rail item - per instruction, no
 * stubs for Home/Agent/Trends/Compare/Briefing. Two items for now: Comp and
 * JD & Skills.
 *
 * Navigation is URL-based (react-router, ADDED 2026-09-18 - it used to be
 * plain component state with no URL change). Each rail item owns a path:
 * /comp, /trends, /compare, /jd-skills, /agent, with the gateway at /.
 * Refreshing now stays on the page you were on instead of dropping back to
 * the gateway - what old Minerva does with its own routes. The vertical
 * rides along as ?stack=<id> (old Minerva's ?stackId=), which is what makes
 * it survive F5; it is deliberately NOT in localStorage, so closing the tab
 * still starts clean (see sharedSelection.js for the August reasoning).
 */
const NAV_ITEMS = [
  {
    key: "comp",
    path: "/comp",
    label: "Comp",
    breadcrumb: "Compensation",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M4 19V5" /><rect x="7" y="11" width="3" height="8" /><rect x="13" y="7" width="3" height="12" /><path d="M4 19h16" />
      </svg>
    ),
  },
  {
    key: "trends",
    path: "/trends",
    label: "Trends",
    breadcrumb: "Job Wages & Inflation Comparison",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M3 17l6-6 4 4 8-8" /><path d="M15 7h6v6" />
      </svg>
    ),
  },
  {
    key: "compare",
    path: "/compare",
    label: "Compare",
    breadcrumb: "Blended Roles",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M12 3v18" /><path d="M5 7h5M5 12h5M5 17h5M14 7h5M14 12h5M14 17h5" />
      </svg>
    ),
  },
  {
    key: "skills",
    path: "/jd-skills",
    label: "JD·Skills",
    breadcrumb: "JD & Skills Comparison",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="3" y="4" width="8" height="16" rx="1.5" /><rect x="13" y="4" width="8" height="16" rx="1.5" /><path d="M6 9h2M6 13h2M16 9h2M16 13h2" />
      </svg>
    ),
  },
  {
    key: "agent",
    path: "/agent",
    label: "Agent",
    breadcrumb: "Job Matching Agent",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="4" y="8" width="16" height="12" rx="3" /><path d="M12 8V4" /><circle cx="12" cy="3" r="1.4" /><circle cx="9" cy="14" r="1.1" /><circle cx="15" cy="14" r="1.1" />
      </svg>
    ),
  },
];

export default function Shell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Login gate (ADDED 2026-09-24): checked once on mount against Flask's
  // session cookie (see backend/auth.py). Rendered IN PLACE of whatever the
  // current URL would normally show, rather than a separate /login route -
  // so a deep link like /comp?stack=4 survives the round trip through
  // Microsoft sign-in and lands the user back exactly where they tried to
  // go. `user` is null while loading OR while logged out; `checked`
  // distinguishes "still finding out" from "confirmed logged out" so we
  // don't flash the login page during the initial fetch.
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    fetch("/api/auth/me")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setChecked(true));
  }, []);

  // Gateway-first entry (18-Sep decision): the launcher is the app's "/"
  // route, full-screen with NO rail; a tile click routes into the shell.
  // The rail's home button goes back to it. It is now tied to the URL, so
  // it shows on a fresh visit to / but NOT on a refresh of a real page -
  // refreshing /comp keeps you on /comp (her 18-Sep requirement; old
  // Minerva behaves the same way).
  const active = NAV_ITEMS.find((n) => n.path === location.pathname) || NAV_ITEMS[0];

  // The vertical follows the user across rail clicks by riding in the query
  // string, so /comp?stack=4 -> Blended Roles keeps Finance. Only the two
  // pages that actually have a single vertical take it; Trends and Skills
  // use their own multi-select filters (max 3), exactly as old Minerva's
  // job-wages / skillset-matrix pages do - confirmed against
  // Existing_Minerva_latest_git\MinervaUI-main.zip, 18 Sep.
  // Pages the vertical travels to. JD·Skills is in the list (ADDED after
  // live testing): Comp and JD·Skills share a selection both ways, and
  // dropping ?stack= on the way over meant coming back to /comp with no
  // stack - which re-opened the "Browse the roles" popup and threw away the
  // role the user had just been working on. The JD page ignores the param;
  // it is there so the round trip preserves context.
  const STACK_PAGES = ["/comp", "/compare", "/jd-skills"];
  // Params that travel with the user: the vertical and the sub stack. The
  // job title deliberately does NOT - on staging, leaving Comp and coming
  // back keeps vertical + sub stack and drops the job title, and we match
  // that behaviour (her call, 18 Sep).
  const CARRIED_PARAMS = ["stack", "sub"];

  function go(path) {
    let carry = "";
    if (STACK_PAGES.includes(path)) {
      const next = new URLSearchParams();
      CARRIED_PARAMS.forEach((k) => {
        const v = searchParams.get(k);
        if (v) next.set(k, v);
      });
      const qs = next.toString();
      if (qs) carry = `?${qs}`;
    }
    // Record where the user is coming from for the destination page. Comp
    // uses it to stay quiet when the user arrives from a page it shares a
    // selection with (Blended Roles, JD·Skills) - see App.js. Deliberately
    // NOT router state: that survives a reload (see navSource.js).
    setNavFrom(location.pathname);
    navigate(`${path}${carry}`);
  }

  // Not logged in (and we've actually heard back from the server, not just
  // still loading): show the login page in place of whatever URL the user
  // is on. Nothing else in Shell renders until this resolves true, so no
  // page ever mounts and fires its data fetches before auth is confirmed.
  if (checked && !user) {
    return <LoginPage />;
  }
  if (!checked) {
    return null;
  }

  if (location.pathname === "/") {
    return (
      <GatewayPage
        onEnter={(key, stackId) => {
          // A gateway tile is its own kind of arrival - record it so a
          // stale "came from Blended Roles" can't suppress Comp's popup.
          setNavFrom("/");
          const item = NAV_ITEMS.find((n) => n.key === key) || NAV_ITEMS[0];
          const carry = stackId != null && STACK_PAGES.includes(item.path) ? `?stack=${stackId}` : "";  // gateway picks a vertical only
          navigate(`${item.path}${carry}`);
        }}
      />
    );
  }

  return (
    <div className="shell">
      <nav className="rail">
        <div className="rail-logo" title="Potentiam">
          <img src="/potentiam_logo.png" alt="Potentiam" />
        </div>
        <div className="rail-nav">
          <button
            type="button"
            className="rail-item"
            title="Home"
            onClick={() => navigate("/")}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h14V9.5" />
            </svg>
            <span>Home</span>
          </button>
          {NAV_ITEMS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`rail-item${active.key === item.key ? " active" : ""}`}
              title={item.breadcrumb}
              onClick={() => go(item.path)}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </div>
        <div className="rail-spacer" />
        <div className="rail-account">
          <button
            type="button"
            className="rail-avatar"
            title="Account"
            onClick={() => setMenuOpen((v) => !v)}
          >
            {(user?.name || "?").trim().charAt(0).toUpperCase()}
          </button>
          {menuOpen && (
            <>
              {/* Full-screen click-catcher to close the menu on outside click -
                  simpler and more robust than a document listener + cleanup
                  for a menu this small. */}
              <div className="rail-account-scrim" onClick={() => setMenuOpen(false)} />
              <div className="rail-account-menu">
                <div className="rail-account-name">{user?.name}</div>
                <div className="rail-account-email">{user?.email}</div>
                <a className="rail-account-logout" href="/auth/logout">Log out</a>
              </div>
            </>
          )}
        </div>
      </nav>

      <div className="main">
        <header className="topbar">
          <div className="tb-left">
            <span className="tb-logo"><img src="/potentiam_logo.png" alt="Potentiam" /></span>
            <span className="tb-div" />
            <span className="platform-mark">Minerva<em>Intelligence</em></span>
            <span className="breadcrumb">
              <span className="active">{active.breadcrumb}</span>
            </span>
          </div>
          {/* Search + notification bell removed per 18-Aug call (neither maps
              to a real Minerva feature). Help stays - a real help page is
              planned; it will be wired to this button. */}
          <div className="tb-right">
            <button type="button" className="icon-btn" title="Help">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1 .9-1 1.7" /><line x1="12" y1="17" x2="12" y2="17" /></svg>
            </button>
          </div>
        </header>

        <div className="shell-content">
          {/* Both pages stay mounted-on-demand: only the active one renders.
              Each is fully self-contained - Comp is App.js untouched. */}
          {active.key === "comp" && <CompPage />}
          {active.key === "trends" && <TrendsPage />}
          {active.key === "compare" && <ComparePage />}
          {active.key === "skills" && <JDSkillsPage />}
          {active.key === "agent" && <AgentPage />}
        </div>
      </div>
    </div>
  );
}
